import { useEffect, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { syncService } from '../services/syncService';
import { db } from '../lib/database';
import { setNetworkStatus } from '../reducers/uiSlice';
import { setSyncInProgress, updatePendingCount, setSyncError, setLastSyncTime } from '../reducers/syncSlice';
import { fetchDashboardStats } from '../reducers/dashboardSlice';
import { fetchForms } from '../reducers/formsSlice';
import { fetchSubmissions } from '../reducers/submissionsSlice';
import { toastService } from '../services/toastService';
import { backgroundSyncService } from '../services/backgroundSyncService';
import { networkService } from '../services/networkService';

export const useSync = () => {
  const dispatch = useDispatch();
  const isOnline = useSelector(state => state.ui?.networkStatus?.isOnline ?? navigator.onLine);
  const syncInProgress = useSelector(state => state.sync?.syncInProgress ?? false);
  const pendingCount = useSelector(state => state.sync?.pendingCount ?? 0);
  const lastSyncTime = useSelector(state => state.sync?.lastSyncTime);

  const isInitialMount = useRef(true);
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0, currentItem: '' });

  const updatePending = useCallback(() => {
    return db.outbox
      .toArray()
      .then(items => {
        // Collect temp entity IDs that already have a linked submission in the queue.
        const entityIdsWithSubmission = new Set(
          items
            .filter(i => i.type === 'submission' && i.payload?.entity_id)
            .map(i => i.payload.entity_id)
        );

        const unique = new Set();
        items.forEach(item => {
          if (item.type === 'file_upload') return; // not a separate logical item
          if (item.type === 'entity') {
            // Already counted via its linked submission — skip.
            if (entityIdsWithSubmission.has(item.tempId)) return;
            unique.add(item.tempId || item.id);
            return;
          }
          const subId = item.type === 'submission' || !item.type
            ? item.tempId
            : item.payload?.submissionId;
          unique.add(subId || item.id);
        });
        dispatch(updatePendingCount(unique.size));
      })
      .catch(error => {
        console.error('Error updating pending count:', error);
      });
  }, [dispatch]);

  const manualSync = useCallback(
    (options = {}) => {
      if (networkService.getNetworkQuality() === 'offline') {
        if (!options.silent) {
          toastService.error('You are offline. Cannot sync.');
        }
        return Promise.reject(new Error('Offline'));
      }
      if (syncInProgress) {
        return Promise.resolve();
      }

      const getItemsToSync = options.items ? Promise.resolve(options.items) : db.outbox.toArray();

      return getItemsToSync
        .then(itemsToSync => {
          if (itemsToSync.length === 0) {
            if (options.showToast && !options.silent) {
              toastService.info('Everything is already up to date.');
            }
            backgroundSyncService.registerBackgroundSync();
            return Promise.resolve();
          }

          dispatch(setSyncInProgress(true));
          setSyncProgress({ current: 0, total: itemsToSync.length, currentItem: 'Starting sync...' });

          const syncPromise = syncService.processOutbox(
            itemsToSync,
            progress => {
              setSyncProgress(progress);
            },
            options.showToast && !options.silent,
            options.skipInitialToast,
            options.toastId
          );

          return syncPromise
            .then(result => {
              const now = new Date().toISOString();
              dispatch(setLastSyncTime(now));
              return db.settings.put({ key: 'lastSyncTime', value: now }).then(() => {
                updatePending();
                dispatch(fetchDashboardStats(true));
                dispatch(fetchForms(true));
                dispatch(fetchSubmissions({ page: 1, limit: 100, forceRefresh: true }));
                return result;
              });
            })
            .catch(err => {
              dispatch(setSyncError(err.message));
              if (options.showToast && !options.silent) {
                toastService.error('Sync failed', { id: options.toastId });
              } else if (options.toastId) {
                toastService.dismiss(options.toastId);
              }
              if (!options.silent) {
                console.error('Sync failed:', err);
              }
              throw err;
            })
            .finally(() => {
              dispatch(setSyncInProgress(false));
              setSyncProgress({ current: 0, total: 0, currentItem: '' });
            });
        })
        .catch(error => {
          if (!options.silent) {
            console.error('Failed to get outbox items:', error);
          }
          throw error;
        });
    },
    [syncInProgress, dispatch, updatePending]
  );

  useEffect(() => {
    const handleOnline = () => {
      dispatch(setNetworkStatus(true));

      db.settings
        .get('autoSync')
        .then(autoSyncSetting => {
          const isAutoSyncEnabled = autoSyncSetting?.value !== false;

          return db.outbox.count().then(pendingItems => {
            if (pendingItems > 0) {
              syncService.reportOfflineStats().finally(() => {
                if (isAutoSyncEnabled) {
                  const toastId = toastService.loading('Connection restored. Syncing pending data...');
                  manualSync({ silent: false, showToast: true, skipInitialToast: true, toastId });
                }
              });
            }
          });
        })
        .catch(error => {
          console.error('Error checking auto-sync setting:', error);
        });
    };

    const handleOffline = () => {
      dispatch(setNetworkStatus(false));
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && navigator.onLine) {
        const currentOnlineStatus = navigator.onLine;
        if (currentOnlineStatus !== isOnline) {
          dispatch(setNetworkStatus(currentOnlineStatus));
          if (currentOnlineStatus) {
            handleOnline();
          }
        }

        import('../utils/formAssignmentHandler').then(({ formAssignmentHandler }) => {
          formAssignmentHandler.validateAndRefresh().catch(() => {});
        });
      }
    };

    if (isInitialMount.current) {
      dispatch(setNetworkStatus(navigator.onLine));
      db.settings.get('lastSyncTime').then(setting => {
        if (setting) dispatch(setLastSyncTime(setting.value));
      });
      updatePending();

      if (navigator.onLine) {
        db.settings.get('autoSync').then(autoSyncSetting => {
          if (autoSyncSetting?.value !== false) {
            db.outbox.count().then(pendingItems => {
              if (pendingItems > 0) manualSync({ silent: true });
            });
          }
        });
      }
      isInitialMount.current = false;
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch, manualSync, updatePending, isOnline]);

  return {
    isOnline,
    syncInProgress,
    pendingCount,
    lastSyncTime,
    manualSync,
    updatePending,
    syncProgress,
    requestNotificationPermission: backgroundSyncService.requestNotificationPermission.bind(backgroundSyncService)
  };
};
